/**
 * Email Verification Screen
 * OTP verification for email before registration
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from 'react-native';
import Icon from '../components/Icon';
import { authAPI } from '../services/authApi';
import { useTheme } from '../context/ThemeContext';

// Debug mode - set to true to bypass OTP verification for testing
const DEBUG_MODE = false; // Set to true if you want to bypass OTP (for testing without backend)

// Backend APIs Required for real OTP:
// POST /api/auth/send-otp (sends OTP to email)  
// POST /api/auth/verify-otp (verifies OTP code)

const EmailVerificationScreen = ({ navigation, route }) => {
  const { colors, isDarkMode } = useTheme();
  const { email, registrationData, otpAlreadySent } = route.params;
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  
  const inputRefs = useRef([]);

  // Timer countdown for resend OTP
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Send OTP initially when screen loads (only if not already sent)
  useEffect(() => {
    if (!otpAlreadySent) {
      sendOTP();
    } else {
      // OTP already sent by register API, just start timer
      setTimer(60);
      setCanResend(false);
    }
  }, []);

  const sendOTP = async () => {
    setIsResending(true);
    try {
      // Debug mode bypass
      if (DEBUG_MODE) {
        console.log('DEBUG MODE: Bypassing OTP send - endpoints not available');
        Alert.alert(
          'Development Mode', 
          `OTP sending bypassed for testing.\n\nEmail: ${email}\n\nTo continue:\n1. Enter any 6-digit code (e.g., 123456)\n2. Click "VERIFY & CONTINUE"\n\nNote: Real OTP emails will work once backend endpoints are implemented.`
        );
        setTimer(60);
        setCanResend(false);
        setIsResending(false);
        return;
      }

      // For resend - call register API again to trigger new OTP
      console.log('📧 Resending OTP by calling register API again...');
      await authAPI.register(registrationData);
      Alert.alert('OTP Resent', `New OTP sent to your email (${email}).`);
      setTimer(60);
      setCanResend(false);
      
    } catch (error) {
      console.error('Resend OTP error:', error);
      Alert.alert('Error', 'Failed to resend OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleOtpChange = (text, index) => {
    if (text.length > 1) return; // Prevent multiple characters
    
    const newOtp = [...otp];
    newOtp[index] = text;
    setOtp(newOtp);

    // Auto-focus next input
    if (text && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyPress = (e, index) => {
    if (e.nativeEvent.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const verifyOTP = async () => {
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      Alert.alert('Error', 'Please enter the complete 6-digit OTP.');
      return;
    }

    setIsLoading(true);
    try {
      // Debug mode bypass
      if (DEBUG_MODE) {
        console.log('DEBUG MODE: Bypassing OTP verification - endpoints not available');
        console.log('OTP entered:', otpString);
        // Accept any 6-digit code in debug mode
        Alert.alert(
          'Debug Mode - Verification Bypassed', 
          `Code "${otpString}" accepted for testing.\n\nProceeding with registration...`
        );
        await completeRegistration();
        return;
      }

      // Use your verify-otp API to complete registration
      const verifyResponse = await authAPI.verifyOTP(email, otpString);
      
      console.log('✅ OTP VERIFIED SUCCESSFULLY');
      console.log('📥 Verify OTP Response:', verifyResponse);
      
      // Check if token is returned directly from verify-otp
      if (verifyResponse && verifyResponse.token) {
        await authAPI.saveToken(verifyResponse.token);
        console.log('✅ Token saved from verify-otp, navigating to Home screen...');
        navigation.replace('MainTabs');
      } else {
        // If no token, complete registration
        console.log('⚠️ No token in verify response, completing registration...');
        await completeRegistration();
      }
      
    } catch (error) {
      console.error('OTP verification error:', error);
      
      // If 404, means OTP endpoints not implemented yet - bypass for now
      if (error.response?.status === 404) {
        console.log('OTP verification endpoint not available, bypassing...');
        Alert.alert(
          'Development Mode',
          'OTP verification bypassed (endpoint not available). Proceeding with registration.',
          [
            {
              text: 'Continue',
              onPress: () => completeRegistration()
            }
          ]
        );
        return;
      }
      
      let errorMessage = 'Invalid OTP. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      Alert.alert('Verification Failed', errorMessage);
      // Clear OTP inputs
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0].focus();
    } finally {
      setIsLoading(false);
    }
  };

  const completeRegistration = async () => {
    try {
      console.log('🔵 COMPLETING REGISTRATION AFTER EMAIL VERIFICATION');
      
      // Ensure phone is provided or use a default
      const finalRegistrationData = {
        ...registrationData,
        phone: registrationData.phone || '0000000000', // Provide default if empty
      };
      
      console.log('📤 Registration payload:', {
        ...finalRegistrationData,
        password: '***', // Hide password in logs
      });
      
      const response = await authAPI.register(finalRegistrationData);
      
      console.log('✅ REGISTRATION SUCCESS AFTER EMAIL VERIFICATION');
      console.log('📥 Full response:', response);
      
      // Save token if registration is successful
      if (response && response.token) {
        await authAPI.saveToken(response.token);
        console.log('✅ Token saved, navigating to Home screen...');
        // Navigate directly to MainTabs (Home screen)
        navigation.replace('MainTabs');
      } else if (response && response.success) {
        console.log('✅ Registration successful, navigating to Login...');
        navigation.navigate('Login');
      } else {
        console.log('✅ Account created, navigating to Login...');
        navigation.navigate('Login');
      }
    } catch (error) {
      console.error('Registration after verification error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response?.status === 400) {
        const errorData = error.response?.data;
        if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        } else {
          errorMessage = 'Invalid registration data. Please check all fields.';
        }
      } else if (error.response?.status === 409) {
        errorMessage = 'Email already exists. Please use a different email address.';
      }
      
      Alert.alert(
        'Registration Failed',
        errorMessage
      );
    }
  };

  const resendOTP = () => {
    if (!canResend) return;
    sendOTP();
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />
      
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.background }]}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}>
          <Icon name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Email Verification</Text>
      </View>

      <View style={styles.content}>
        {/* Title and Description */}
        <View style={styles.titleSection}>
          <Text style={[styles.title, { color: colors.text }]}>Verify Your Email</Text>
          <Text style={[styles.description, { color: colors.textSecondary }]}>
            Please check your email for the 6-digit verification code
          </Text>
          <Text style={[styles.email, { color: colors.primary }]}>{email}</Text>
          <Text style={[styles.description, { color: colors.textSecondary }]}>
            Enter the code below to continue
          </Text>
        </View>

        {/* OTP Input */}
        <View style={styles.otpContainer}>
          {otp.map((digit, index) => (
            <TextInput
              key={index}
              ref={(ref) => (inputRefs.current[index] = ref)}
              style={[
                styles.otpInput,
                { 
                  color: colors.text || '#FFFFFF', 
                  borderColor: digit ? colors.primary : colors.border,
                  backgroundColor: isDarkMode ? '#2A2A2A' : '#F5F5F5'
                }
              ]}
              value={digit}
              onChangeText={(text) => handleOtpChange(text, index)}
              onKeyPress={(e) => handleKeyPress(e, index)}
              keyboardType="numeric"
              textAlign="center"
              maxLength={1}
              selectTextOnFocus
              placeholderTextColor={colors.textSecondary || '#999999'}
            />
          ))}
        </View>

        {/* Timer and Resend */}
        <View style={styles.resendSection}>
          {!canResend ? (
            <Text style={[styles.timerText, { color: colors.textSecondary }]}>
              Resend code in {timer}s
            </Text>
          ) : (
            <TouchableOpacity onPress={resendOTP} disabled={isResending}>
              <Text style={[styles.resendText, { color: colors.primary }]}>
                {isResending ? 'Sending...' : 'Resend Code'}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Verify Button */}
        <TouchableOpacity
          style={[
            styles.verifyButton,
            { backgroundColor: colors.primary },
            (isLoading || otp.join('').length !== 6) && { opacity: 0.6 }
          ]}
          onPress={verifyOTP}
          disabled={isLoading || otp.join('').length !== 6}>
          {isLoading ? (
            <ActivityIndicator color={colors.buttonText || '#FFFFFF'} />
          ) : (
            <Text style={[styles.verifyButtonText, { color: colors.buttonText || '#FFFFFF' }]}>
              VERIFY & CONTINUE
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingTop: 45,
  },
  backButton: {
    marginRight: 16,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 40,
  },
  titleSection: {
    alignItems: 'center',
    marginBottom: 40,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 16,
  },
  description: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
  },
  email: {
    fontSize: 16,
    fontWeight: '600',
    marginVertical: 8,
  },
  otpContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
    paddingHorizontal: 10,
  },
  otpInput: {
    width: 50,
    height: 60,
    borderWidth: 2,
    borderRadius: 12,
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
    color: '#FFFFFF', // Ensure text is always visible
  },
  resendSection: {
    alignItems: 'center',
    marginBottom: 40,
  },
  timerText: {
    fontSize: 16,
  },
  resendText: {
    fontSize: 16,
    fontWeight: '600',
  },
  verifyButton: {
    height: 52,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  verifyButtonText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
});

export default EmailVerificationScreen;
